import dayjs, { type Dayjs } from 'dayjs';
import { isDefined, mergeDeep, pick } from 'remeda';
import type { CompanyConfiguration } from 'shared/hooks/use-load-company-configuration';
import { treatInvoice } from 'shared/serializer';
import { getCgv } from 'shared/utils/selectors';
import type { Debtor } from 'types/store/client-state';
import type { CreditNote } from 'types/store/credit-note-state';
import type { Invoice, LightInvoice } from 'types/store/invoice-state';

import type { CustomProductLine, InvoiceFormFieldValues } from './InvoiceForm.types';

const FORM_DATE_FORMAT = 'YYYY-MM-DD';

const NOW = dayjs();

export const getDefaultIssueDate = () => NOW;

export const getDefaultDueDate = (company?: CompanyConfiguration) =>
  NOW.add(getCgv(company)?.minimum_payment_terms_delay || 0, 'day');

export const toFormDate = (value: Dayjs | string | Date) => dayjs(value).format(FORM_DATE_FORMAT);

export const toFormDateOptional = (value: Dayjs | string | Date | null | undefined) =>
  value == null ? null : toFormDate(value);

export const isNoTva = (debtor?: Debtor) => debtor?.intracom || debtor?.co_contractor;

export const applyCreditNoteSign = <T extends number | string>(isCreditNote: boolean, value: T) =>
  isCreditNote && isDefined(value) && value !== '' ? -Math.abs(Number(value)) : value;

export const getCurrency = (
  company?: CompanyConfiguration,
  debtor?: Debtor,
  values?: Invoice | CreditNote,
) => values?.currency || debtor?.currency || company?.currency;

export const getInvoicesForReference = (data: any, references: string[]) =>
  data
    .map((e: any) => treatInvoice<LightInvoice>(e))
    .filter((e: any) => references.includes(String(e.reference)));

export const getInvoicesForDebtor = (filteredInvoices: LightInvoice[], debtor: Debtor) =>
  filteredInvoices.filter(e => Number(e.debtor_attributes.id) === Number(debtor.id));

const getBillingLogId = (id?: number) => {
  if (!id) {
    return undefined;
  }

  return {
    billing_log_id: id,
  };
};

const getDate = (key: keyof InvoiceFormFieldValues, date?: Dayjs) => {
  if (!date) return undefined;
  return { [key]: toFormDate(date) };
};

const getMatchedPayments = <T extends Invoice | CreditNote>({ matched_payments_attributes }: T) => {
  if (!matched_payments_attributes) {
    return undefined;
  }

  return {
    invoices_to_link: matched_payments_attributes.map(({ id }) => id),
  };
};

export const getTotalHtva = ({
  quantity,
  discount_rate,
  product_attributes: { current_unity_price_htva = 0 },
}: CustomProductLine) =>
  Number(quantity) * Number(current_unity_price_htva) * (1 - (Number(discount_rate) || 0) / 100);

export const getTotalTvac = (productLine: CustomProductLine, tvaRate: number | string) =>
  getTotalHtva(productLine) * (1 + Number(tvaRate) / 100);

const getProductLines = <T extends Invoice | CreditNote>({ product_lines_attributes }: T) => ({
  product_lines_attributes: product_lines_attributes.map(e => ({
    ...e,
    isInvoice: !!e.linked_invoice_id,
  })),
});

const getObjFormValues = <T extends Invoice | CreditNote>(obj: T) => ({
  ...pick(obj, ['id', 'debtor_attributes', 'comment', 'order_reference']),
  ...getDate('issue_date', obj.issue_date),
  ...getDate('due_date', obj.due_date),
  ...getBillingLogId(obj?.billing_log_attributes?.id),
  ...getMatchedPayments(obj),
  ...getProductLines(obj),
});

export const getCreditNoteFormValues = (
  creditNote?: CreditNote,
): Partial<InvoiceFormFieldValues> => (creditNote ? getObjFormValues<CreditNote>(creditNote) : {});

export const getInvoiceFormValues = (invoice?: Invoice): Partial<InvoiceFormFieldValues> =>
  invoice ? getObjFormValues<Invoice>(invoice) : {};

export const getProductLine = (
  noTva: boolean,
  data: Partial<CustomProductLine> = {},
): Partial<CustomProductLine> =>
  mergeDeep(
    {
      quantity: 1,
      total_htva: 0,
      total_tvac: 0,
      discount_rate: 0,
      product_attributes: {
        name: '',
        tax_category_code: noTva ? '00' : '',
        current_unity_price_htva: 0,
      },
    },
    data,
  );
